import trimStart from 'lodash/trimStart'
import cookies from 'js-cookie'
import uuidv4 from 'uuid/v4'
import { normalizeSellablePricing } from '#src/services/sellable'
import { Analytics, UserTraits } from '#src/tracking'
import {
  MyselfFragment,
  TrackableOrderFragment,
  TrackableSellableFragment,
  ContactType
} from '#src/generated/graphql'
import { format } from 'date-fns'
import { OrderTrackingProperties, SellableTrackingProperties } from '#src/tracking/trackingEvents'
import config from '#src/config'

export interface UserAcquisitionAttributes {
  adId?: string
  adgroup?: string
  adgroupId?: string
  adPosition?: string
  adType?: string
  campaign?: string
  campaignId?: string
  clientSessionId?: string
  content?: string
  creativeId?: string
  department?: string
  device?: string
  deviceModel?: string
  expertId?: string
  geoLocation?: string
  googleClickId?: string
  facebookClickId?: string
  initAppUrl?: string
  interestLocation?: string
  keyword?: string
  matchType?: string
  medium?: string
  network?: string
  placement?: string
  productId?: string
  referrerId?: string
  referrerUrl?: string
  searchQuery?: string
  source?: string
  targetId?: string
  term?: string
}

/**
 * URL tracking parameters
 *
 * This is a map of the format used in the query string to the format used by our attribution/analytics tools
 */
export const USER_ACQUISITION_QUERY_PARAM_MAP: {
  [queryParam: string]: keyof UserAcquisitionAttributes
} = {
  ad_id: 'adId',
  ad_type: 'adType',
  ad_search_query: 'searchQuery',
  adgroup_id: 'adgroupId',
  expert_id: 'expertId',
  campaign_id: 'campaignId',
  department: 'department',
  gclid: 'googleClickId',
  fbclid: 'facebookClickId',
  product_id: 'productId',
  referrer_id: 'referrerId',
  target_id: 'targetId',
  utm_adgroup: 'adgroup',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_term: 'term',
  utm_content: 'content',
  utm_source: 'source',
  utm_id: 'campaignId',
  vt_match_type: 'matchType',
  vt_device: 'device',
  vt_device_model: 'deviceModel',
  vt_geo_location: 'geoLocation',
  vt_interest_location: 'interestLocation',
  vt_placement: 'placement',
  vt_target_id: 'targetId',
  vt_ad_position: 'adPosition',
  vt_network: 'network'
}

export const DEFAULT_USER_ACQUISITION_ATTRIBUTES: UserAcquisitionAttributes = {
  source: 'organic'
}

/**
 * Converts our internal UserTraits data into a format understood by the Facebook pixel for
 *   use with Facebook Analytics (via `setUserProperties`)
 *
 * See: https://developers.facebook.com/docs/facebook-pixel/implementation/user-properties#predefined-user-properties
 */
export function getFacebookUserPropertiesFromUserTraits(traits: UserTraits) {
  return {
    $email: traits.email,
    $city: traits.address && traits.address.city,
    $state: traits.address && traits.address.state,
    $country: traits.address && traits.address.country,
    $zipcode: traits.address && traits.address.postalCode,
    $gender: traits.gender,
    $language: traits.language
  }
}

/**
 * Converts our internal UserTraits data into a format understood by the Facebook pixel during
 *   pixel initialization.
 *
 * See: https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching#visitor-data
 */
export function getFacebookAdvancedMatchingDataFromUserTraits(traits: UserTraits) {
  return {
    ct:
      traits.address && traits.address.city && traits.address.city.toLowerCase().replace(/\s/g, ''),
    // TODO: Normalize state to two-letter state code
    st: traits.address && traits.address.city && traits.address.city.toLowerCase(),
    // TODO: Normalize country to two-letter country code
    country: traits.address && traits.address.country,
    zp: traits.address && traits.address.postalCode,
    db: traits.dateOfBirth && format(traits.dateOfBirth, 'yyyyMMdd'),
    em: traits.email && traits.email.toLowerCase(),
    fn: traits.firstName && traits.firstName.toLowerCase(),
    ln: traits.lastName && traits.lastName.toLowerCase(),
    ge: traits.gender,
    ph: traits.phone && traits.phone.replace(/[^\d]/g, '')
  }
}

export function getFacebookContentsFromSellableTrackingProperties(
  sellable: SellableTrackingProperties & { quantity?: number }
) {
  return {
    id: sellable.product_id,
    quantity: sellable.quantity || 1,
    item_price: sellable.price || undefined
  }
}

export function getUserAcquisitionAttributesFromQueryString(
  queryStringOrParams: string | URLSearchParams
): UserAcquisitionAttributes {
  // Trim the leading "?" before parsing
  const params =
    typeof queryStringOrParams === 'string'
      ? new URLSearchParams(trimStart(queryStringOrParams, '?'))
      : queryStringOrParams
  const attributes: UserAcquisitionAttributes = {}

  // @ts-ignore URLSearchParams is not "Array-like" in TypeScript, but this works in all major browsers
  const pairs = Array.from(params)
  for (const [queryParamKey, queryParamValue] of pairs) {
    const attributeKey: keyof UserAcquisitionAttributes | undefined =
      USER_ACQUISITION_QUERY_PARAM_MAP[queryParamKey]

    if (attributeKey) {
      attributes[attributeKey] = queryParamValue
    }
  }

  /**
   * Add session ID into the user attributes
   */
  attributes.clientSessionId = getClientSessionId()

  /**
   * Determine the acquisition channel
   *
   * The defacto indicator is `utm_source`, but Google with valueTrack does not use UTM params.
   * We can instead detect paid Google traffic by looking for the existence of a `gclid` param.
   */
  if (params.has('gclid')) {
    attributes.source = 'google'
  }

  return attributes
}

export function getUserAcquisitionAttributesFromWindow(window: Window): UserAcquisitionAttributes {
  return {
    referrerUrl: window.document.referrer,
    initAppUrl: window.location.href,
    ...getUserAcquisitionAttributesFromQueryString(window.location.search)
  }
}

export function getUserAcquisitionAttributes(): UserAcquisitionAttributes {
  if (process.env.TARGET !== 'web') {
    return {}
  }

  const stringified = localStorage.getItem('acquisition_attrs')
  const attributes: UserAcquisitionAttributes = stringified ? JSON.parse(stringified) : {}

  return attributes
}

export function setUserAcquisitionAttributes(
  attrs: UserAcquisitionAttributes,
  myself?: MyselfFragment,
  analytics?: Analytics
) {
  if (process.env.TARGET !== 'web') {
    return
  }

  // N.B: Ideally the default attributes would not be persisted and would instead be added by the
  //  getter method (`getUserAcquisitionAttributes`). This isn't possible because we also persist
  //  the attributes to a cookie (`deal_ust`), which the browser may send to the API at any time.
  const existingAttrs = getUserAcquisitionAttributes()
  const updatedAttrs = Object.assign({}, DEFAULT_USER_ACQUISITION_ATTRIBUTES, existingAttrs, attrs)

  // Persist the attributes to a cookie.
  //
  //  The cookie is sent along with each request, including registration. During registration,
  //  the attributes are saved to the database for use by backend systems. After registration,
  //  we remove the cookie in order to reduce API request sizes (the backend does not update
  //  the attributes in the database once the user has been created).
  if (!myself) {
    cookies.set('deal_ust', JSON.stringify(updatedAttrs), {
      domain: config.get('consumer.clientSideCookieDomain')
    })
  } else {
    cookies.remove('deal_ust', {
      domain: config.get('consumer.clientSideCookieDomain')
    })
  }

  // Persist the attributes to local storage, too.
  //
  //  This copy is meant to be as permanent as possible, and support cross-device attribution.
  //  After a user is registered, we request the persisted attributes from the API and store
  //  it to local storage (meaning new devices will be synchronized with the user's other devices)
  localStorage.setItem('acquisition_attrs', JSON.stringify(updatedAttrs))

  // HACK: When the acquisition department is first set, track all the previously fired events
  //   to the pixel for that department.
  //
  // This implementation is succeptible to race conditions ("analytics" is optional), and should
  //   live in a consolidated tracking service
  if (analytics && attrs.department && !existingAttrs.department) {
    analytics.flushDepartmentFacebookEvents(attrs.department)
  }
}

/**
 * Determine if we should reset the user's session ID or not
 *
 * There are four cases in which we regenerate the session id
 * 1. A utm_source is present in the url
 * 2. A gclid is present in the url
 * 3. A fbclid is present in the url
 * 4. An existing session id isn't present in the cookie (first time)
 */
export function shouldClientSessionReset(queryStringOrParams: string | URLSearchParams) {
  const params =
    typeof queryStringOrParams === 'string'
      ? new URLSearchParams(trimStart(queryStringOrParams, '?'))
      : queryStringOrParams

  return (
    params.has('utm_source') || params.has('gclid') || params.has('fbclid') || !getClientSessionId()
  )
}

export function getClientSessionId() {
  return cookies.get('curated_client_session_id')
}

export function setClientSessionId(analyticsProxy: Analytics) {
  const generatedSessionId = uuidv4()

  analyticsProxy.clientSessionReset({
    previous_id: getClientSessionId(),
    current_id: generatedSessionId
  })

  cookies.set('curated_client_session_id', generatedSessionId)
}

/**
 * Common tracking properties for Sellables.
 *
 * Note:
 *   - `product_id` is the parent sellable ID (so that different variants of the same sellable are tracked under one ID)
 *   - `category_id` and `brand_id` are not part of the Segment spec, but are more concrete/persistent than display names
 *   - Only the first category and first image URL are tracked, since tracking properties should be kept flat
 *
 * See: https://segment.com/docs/spec/ecommerce/v2/#core-ordering
 */
export function getSellableEcommerceProperties(
  sellable: TrackableSellableFragment,
  additionalData?: {}
): SellableTrackingProperties {
  const sellableCategory = sellable.categories[0]
  const parentCategory = sellable.department
    ? sellable.department.topLevelCategory
    : sellableCategory
  const normalizedPricing = normalizeSellablePricing(sellable)

  return {
    brand: sellable.brand ? sellable.brand.displayName : undefined,
    brand_id: sellable.brand ? sellable.brand.id : undefined,
    category: sellableCategory.slug,
    category_id: sellableCategory.id,
    currency: 'USD',
    image_url: sellable.images.length ? sellable.images[0].url : undefined,
    name: sellable.title,
    parent_category: parentCategory ? parentCategory.slug : undefined,
    parent_category_id: parentCategory ? parentCategory.id : undefined,
    parent_sellable_id: sellable.parent ? sellable.parent.id : sellable.id,
    product_id: sellable.feedId,
    price: normalizedPricing && normalizedPricing.price.amount,
    sellable_id: sellable.id,
    sku: sellable.googleId,
    url: `/sellable/${sellable.id}`,
    ...additionalData
  }
}

export function getOrderEcommerceProperties(
  order: TrackableOrderFragment
): OrderTrackingProperties {
  const products: OrderTrackingProperties['products'] = []
  const promotionName = order.appliedPromotionEntry
    ? order.appliedPromotionEntry.promotion.code
    : undefined

  order.lineItems.forEach(item => {
    if (item.source.__typename !== 'OrderLineItemSellableSource') {
      return null
    }

    // TODO: Track AdHoc sources
    products.push({
      quantity: item.quantity,
      ...getSellableEcommerceProperties(item.source.sellable)
    })
  })

  return {
    coupon: promotionName,
    currency: 'USD',
    order_id: order.id,
    products,
    shipping: order.totalShippingCost ? order.totalShippingCost.amount : null,
    subtotal: order.subtotal.amount,
    tax: order.tax ? order.tax.amount : null,
    tip: order.tip ? order.tip.amount : null,
    total: order.totalCost.amount,
    value: order.totalCost.amount
  }
}

/**
 * Common tracking attributes reported for lead requests
 */
export function getUserRequestAttributes(myself?: MyselfFragment) {
  if (!myself) {
    return {
      city: null,
      email: null,
      first_name: null,
      last_name: null,
      phone: null,
      state: null,
      zip: null
    }
  }

  const userPhone = myself.user.contacts.find(contact => contact.type === ContactType.PHONE)
  const userEmail = myself.user.contacts.find(contact => contact.type === ContactType.EMAIL)
  const userAddress = myself.user.address || null

  return {
    city: userAddress && userAddress.city ? userAddress.city : null,
    email: userEmail ? userEmail.value : null,
    first_name: myself.realUser.firstName || null,
    last_name: myself.realUser.lastName || null,
    phone: userPhone ? userPhone.value : null,
    state: userAddress && userAddress.state ? userAddress.state : null,
    zip: userAddress && userAddress.postalCode ? userAddress.postalCode : null
  }
}

/**
 * Generate a referreral link given any base link
 */
export function generateReferralLink(
  link: string,
  referrerId: string,
  expertId?: string,
  additionalParams?: string
) {
  let urlBase = ''
  if (link.includes('?')) {
    urlBase = '&'
  } else {
    urlBase = '?'
  }

  return `${link}${urlBase}referrer_id=${referrerId}${
    expertId ? `&expert_id=${expertId}` : ''
  }&${additionalParams}`
}
